import * as React from 'react';
import styled, { css } from 'styled-components';
import IconButton from '@mui/material/IconButton';
import {
	tabletPortraitMinWidth,
	darkGreyColour,
	lightGreyColour,
	desktopMinWidth,
	darkerSlateGrey,
	greyBackground,
	boxShadow,
	cardBorder,
	superLightGreyColour,
	whiteColour,
	newContentPadding,
	cardLargeInnerGap,
	cardInnerGap
} from '../../cssConstants';
import useCardArrowIcon from './hooks/useCardArrowIcon';

// Use this as default Card layout
export const NewCardWrapper = styled.div<{ $noPadding?: boolean; $innerGap?: null | 'small' | 'large' }>`
	display: flex;
	flex-direction: column;
	width: 100%;
	border: ${cardBorder};
	border-radius: 12px;
	box-shadow: ${boxShadow};
	background-color: ${whiteColour};
	overflow: hidden;
	position: relative;
	padding: ${({ $noPadding }) => ($noPadding ? '0' : newContentPadding)};
	gap: ${({ $innerGap }) => {
		if ($innerGap === 'small') return cardInnerGap;
		if ($innerGap === 'large') return cardLargeInnerGap;
		return '0';
	}};
`;

export const NewCardTitle = styled.h3`
	margin: 0;
	font-size: 16px;
	font-weight: 500;
	line-height: 20px;
	letter-spacing: 0.1px;
	color: ${darkerSlateGrey};
`;

export const NewCardDescription = styled.p<{ $centred?: boolean }>`
	margin: 0;
	font-size: 14px;
	font-weight: 400;
	line-height: 21px;
	letter-spacing: 0.5px;
	text-align: ${({ $centred }) => ($centred ? 'center' : 'left')};
	color: ${superLightGreyColour};

	strong {
		font-weight: 500;
	}
`;

// TODO: Duplicate with above NewCardWrapper, will need to merge
export const OldCardContainer = styled.div`
	display: flex;
	align-items: start;
	width: 100%;
	border: ${cardBorder};
	border-radius: 12px;
	position: relative;
	box-shadow: ${boxShadow};
	background-color: ${whiteColour};
	align-items: stretch;
`;

export const TextContainer = styled.div`
	flex: 2;
	display: flex;
	flex-direction: column;
	justify-content: center;
	padding: 16px;
`;

export const OldCardTitle = styled.h2`
	margin: 0 0 5px 0;
	padding: 0;
	font-size: 16px;
	font-weight: 500;
	line-height: 24px;
	color: ${darkerSlateGrey};
	letter-spacing: 0.15px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 22px;
		line-height: 28px;
	}
`;

export const OldChildCardTitle = styled.h3`
	margin: 0;
	padding: 0;
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	color: ${darkerSlateGrey};
	letter-spacing: 0.1px;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 16px;
		line-height: 24px;
	}
`;

export const greySubTextCss = css`
	font-size: 11px;
	line-height: 16px;
	letter-spacing: 0.5px;
	color: ${superLightGreyColour};
	font-weight: 500;
	margin: 0;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 12px;
	}
`;

export const SubText = styled.p`
	font-size: 12px;
	font-weight: 400;
	line-height: 16px;
	letter-spacing: 0.4px;
	color: ${lightGreyColour};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 14px;
		line-height: 20px;
	}
`;

export const BadgeText = styled.span`
	font-size: 10px;
	font-weight: 500;
	line-height: 16px;
	letter-spacing: 0.5px;
	color: ${whiteColour};

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 12px;
		line-height: 20px;
	}
`;

export const ProgressStatus = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

export const ProgressLineWrapper = styled.div`
	width: 146px;
	height: 8px;
	flex-grow: 1;
	position: relative;
	overflow: hidden;
`;

export const ProgressStatusText = styled.span<{ width?: string }>`
	font-size: 11px;
	font-weight: 500;
	line-height: 16px;
	letter-spacing: 0.5px;
	color: ${darkGreyColour};
	width: ${({ width }) => width || '36px'};
	text-align: end;

	@media (min-width: ${tabletPortraitMinWidth}) {
		font-size: 12px;
	}
	@media (min-width: ${desktopMinWidth}) {
		font-size: 17px;
	}
`;

export const LogoContainer = styled.div`
	/* Add padding at the bottom to make room for the button */
	padding: 16px 4px 50px 4px;
	flex: 1;
	width: 125px;
	text-align: end;
`;

export const Logo = styled.img`
	max-width: 106px;
	max-height: auto;
	margin-right: 22px;
`;

export const FlightPlanIcon = styled(Logo)`
	padding-right: 10px;
`;

// TODO: Should use React Link for internal page links
export const CardButton = styled(IconButton)`
	position: absolute;
	bottom: ${newContentPadding};
	right: ${newContentPadding};
	z-index: 1;
	background-color: ${({ theme }) => theme.cardButtonBackground || greyBackground};
	border-radius: ${({ theme }) => theme.cardButtonRadius || '12px'};
	padding: ${({ theme }) => theme.cardButtonPadding || '12px'};
`;

export const ButtonIcon = styled.img`
	width: ${({ theme }) => theme.cardButtonIconWidth || '24px'};
	height: ${({ theme }) => theme.cardButtonIconHeight || '24px'};
`;

interface CardArrowButtonProps {
	onClick?: (e?: React.MouseEvent) => void | Promise<void>;
	className?: string;
}

export const CardArrowButton = ({ onClick, className }: CardArrowButtonProps) => {
	const arrowIcon = useCardArrowIcon();

	return (
		<CardButton disableRipple disableFocusRipple onClick={onClick} aria-label="forward" className={className}>
			<ButtonIcon src={arrowIcon} alt="forward icon" />
		</CardButton>
	);
};
